import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from './Toast';

/**
 * Sub-session D Step 15: Modal to stop a recurring service.
 *
 * Props:
 *   isOpen           : boolean
 *   onClose          : () => void
 *   onSuccess        : () => void  (called after successful stop, e.g. to refresh lists)
 *   recurringService : { id, service_type, facility_name, client_name, ... }
 *
 * Behavior:
 *   - Date picker for stop_date (default = end of current month, local time)
 *   - Live preview of future WOs that would be affected
 *   - Radio: cancel future WOs vs keep them
 *   - Optional reason
 *   - Calls PATCH /api/recurring/:id/stop (D.8 backend)
 */
const REASON_OPTIONS = [
  { value: '', label: '— Select a reason —' },
  { value: 'customer_request', label: 'Customer requested cancellation' },
  { value: 'frequency_change', label: 'Replaced with new pattern (frequency changed)' },
  { value: 'contract_ended', label: 'Contract ended / not renewed' },
  { value: 'service_quality', label: 'Service quality issue' },
  { value: 'pricing_change', label: 'Pricing renegotiation' },
  { value: 'facility_closed', label: 'Facility closed / inactive' },
  { value: 'other', label: 'Other (please specify in notes)' },
];

const StopRecurringModal = ({ isOpen, onClose, onSuccess, recurringService }) => {
  const todayLocalYMD = () => {
    const d = new Date();
    return d.getFullYear() + '-' +
           String(d.getMonth() + 1).padStart(2, '0') + '-' +
           String(d.getDate()).padStart(2, '0');
  };

  const endOfMonthYMD = () => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return last.getFullYear() + '-' +
           String(last.getMonth() + 1).padStart(2, '0') + '-' +
           String(last.getDate()).padStart(2, '0');
  };

  const [stopDate, setStopDate] = useState(endOfMonthYMD());
  const [cancelFutureWos, setCancelFutureWos] = useState(false);
  const [reason, setReason] = useState('');
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [futureWos, setFutureWos] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadFutureWos = useCallback(async () => {
    if (!recurringService?.id) return;
    setLoadingPreview(true);
    try {
      const res = await api.get('/service-orders', {
        params: { recurring_service_id: recurringService.id, status: 'scheduled' }
      });
      const arr = Array.isArray(res.data) ? res.data : [];
      // Only WOs scheduled AFTER the stop_date are at risk
      const filtered = arr.filter(o => o.scheduled_date > stopDate);
      setFutureWos(filtered);
    } catch (err) {
      console.warn('Preview load failed:', err.message);
      setFutureWos([]);
    } finally {
      setLoadingPreview(false);
    }
  }, [recurringService, stopDate]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStopDate(endOfMonthYMD());
      setCancelFutureWos(false);
      setReason('');
      setCancellationNotes('');
      setFutureWos([]);
    }
  }, [isOpen]);

  // Reload preview when stop_date changes
  useEffect(() => {
    if (isOpen) loadFutureWos();
  }, [isOpen, stopDate, loadFutureWos]);

  const handleSubmit = async () => {
    if (!recurringService?.id) return;
    if (!reason) {
      toast.error('Please select a reason for stopping this pattern.');
      return;
    }
    if (reason === 'other' && !cancellationNotes.trim()) {
      toast.error('When selecting "Other", please provide details in Additional notes.');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date();
      const localDate = todayLocalYMD();
      const localTime = String(today.getHours()).padStart(2, '0') + ':' +
                       String(today.getMinutes()).padStart(2, '0') + ':' +
                       String(today.getSeconds()).padStart(2, '0');

      const payload = {
        stop_date: stopDate,
        cancel_future_wos: cancelFutureWos,
        reason: reason,
        cancellation_notes: cancellationNotes.trim() || null,
        local_date: localDate,
        local_time: localTime,
      };

      const res = await api.patch(
        `/recurring/${recurringService.id}/stop`,
        payload
      );

      const cancelledCount = (res.data?.cancelled_wos || []).length;
      const keptCount = (res.data?.kept_wos || []).length;
      let msg = '✅ Recurring service stopped on ' + stopDate;
      if (cancelFutureWos && cancelledCount > 0) {
        msg += '. Cancelled ' + cancelledCount + ' future WO(s).';
      } else if (keptCount > 0) {
        msg += '. ' + keptCount + ' future WO(s) kept scheduled.';
      }
      toast.success(msg);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to stop recurring service');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const wosCount = futureWos.length;
  const ctaLabel = cancelFutureWos
    ? (wosCount > 0 ? 'Stop & Cancel ' + wosCount + ' WO(s)' : 'Stop Service')
    : (wosCount > 0 ? 'Stop & Keep ' + wosCount + ' WO(s)' : 'Stop Service');
  const ctaColor = cancelFutureWos ? '#DC2626' : '#D97706';

  return (
    <div style={S.backdrop} onClick={() => !submitting && onClose()}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <span style={{fontSize: 22}}>⛔</span>
            <div>
              <div style={S.title}>Stop Recurring Service</div>
              {recurringService && (
                <div style={S.subtitle}>
                  {recurringService.service_type || 'Service'}
                  {recurringService.facility_name ? ' — ' + recurringService.facility_name : ''}
                  {recurringService.client_name ? ' (' + recurringService.client_name + ')' : ''}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} disabled={submitting} style={S.closeBtn}>✕</button>
        </div>

        <div style={S.body}>
          <div style={S.field}>
            <label style={S.label}>Stop Date</label>
            <input
              type="date"
              value={stopDate}
              onChange={(e) => setStopDate(e.target.value)}
              style={S.input}
            />
            <div style={S.hint}>
              The recurring service will end on this date. Visits scheduled on or before this date are NOT affected.
            </div>
          </div>

          <div style={S.previewBox}>
            <div style={S.previewHeader}>
              📋 Future WOs after this date
              <span style={S.countPill}>{loadingPreview ? '...' : wosCount}</span>
            </div>
            {loadingPreview ? (
              <div style={S.previewEmpty}>Loading...</div>
            ) : wosCount === 0 ? (
              <div style={S.previewEmpty}>No future WOs scheduled after this date.</div>
            ) : (
              <div style={S.previewList}>
                {futureWos.slice(0, 8).map(o => (
                  <div key={o.id} style={S.previewRow}>
                    <span style={S.woNum}>{o.order_number}</span>
                    <span style={S.woDate}>{o.scheduled_date}</span>
                    <span style={S.woTech}>
                      {o.tech_first_name ? o.tech_first_name + ' ' + (o.tech_last_name || '') : 'Unassigned'}
                    </span>
                  </div>
                ))}
                {wosCount > 8 && (
                  <div style={S.previewMore}>... and {wosCount - 8} more</div>
                )}
              </div>
            )}
          </div>

          {wosCount > 0 && (
            <div style={S.field}>
              <label style={S.label}>What to do with these {wosCount} future WO(s)?</label>
              <div style={S.radioGroup}>
                <label style={{...S.radio, background: !cancelFutureWos ? '#FEF3C7' : '#F9FAFB', borderColor: !cancelFutureWos ? '#D97706' : '#E5E7EB'}}>
                  <input
                    type="radio"
                    name="action"
                    checked={!cancelFutureWos}
                    onChange={() => setCancelFutureWos(false)}
                  />
                  <div>
                    <div style={S.radioTitle}>Keep them scheduled</div>
                    <div style={S.radioDesc}>WOs remain on the calendar; you'll have to handle each manually.</div>
                  </div>
                </label>
                <label style={{...S.radio, background: cancelFutureWos ? '#FEE2E2' : '#F9FAFB', borderColor: cancelFutureWos ? '#DC2626' : '#E5E7EB'}}>
                  <input
                    type="radio"
                    name="action"
                    checked={cancelFutureWos}
                    onChange={() => setCancelFutureWos(true)}
                  />
                  <div>
                    <div style={S.radioTitle}>Cancel them all</div>
                    <div style={S.radioDesc}>All {wosCount} scheduled WO(s) after stop date will be marked cancelled.</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div style={S.field}>
            <label style={S.label}>Reason <span style={{color: '#DC2626'}}>*</span></label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={S.input}
            >
              {REASON_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} disabled={opt.value === ''}>{opt.label}</option>
              ))}
            </select>
            <div style={S.hint}>Why is this pattern being stopped? Required for audit trail.</div>
          </div>

          <div style={S.field}>
            <label style={S.label}>
              Additional notes
              {reason === 'other' && <span style={{color: '#DC2626'}}> *</span>}
            </label>
            <textarea
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              rows={2}
              placeholder={reason === 'other' ? 'Please specify the reason in detail…' : 'Optional: additional context for this cancellation'}
              style={{...S.input, resize: 'vertical', fontFamily: 'inherit'}}
            />
          </div>
        </div>

        <div style={S.footer}>
          <button onClick={onClose} disabled={submitting} style={S.btnCancel}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{...S.btnPrimary, background: ctaColor}}
          >
            {submitting ? 'Stopping...' : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const S = {
  backdrop: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 },
  modal:    { background:'white', borderRadius:12, width:'100%', maxWidth:560, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 10px 40px rgba(0,0,0,0.3)' },
  header:   { padding:'16px 20px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#FAFAFA' },
  title:    { fontSize:16, fontWeight:700, color:'#1F2937' },
  subtitle: { fontSize:12, color:'#6B7280', marginTop:2 },
  closeBtn: { background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#6B7280', padding:'4px 8px' },
  body:     { padding:'20px', overflow:'auto', flex:1 },
  field:    { marginBottom:16 },
  label:    { display:'block', fontWeight:600, fontSize:13, color:'#374151', marginBottom:6 },
  input:    { width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:14, boxSizing:'border-box' },
  hint:     { fontSize:11, color:'#888', marginTop:4 },
  previewBox: { background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8, marginBottom:16, overflow:'hidden' },
  previewHeader: { padding:'10px 14px', background:'#F3F4F6', fontWeight:600, fontSize:13, color:'#374151', display:'flex', justifyContent:'space-between', alignItems:'center' },
  countPill: { background:'#DBEAFE', color:'#1E40AF', padding:'2px 10px', borderRadius:12, fontSize:11, fontWeight:700 },
  previewEmpty: { padding:'14px', textAlign:'center', color:'#9CA3AF', fontSize:13 },
  previewList: { padding:'4px 0' },
  previewRow:  { padding:'8px 14px', display:'flex', justifyContent:'space-between', fontSize:12, color:'#374151', borderTop:'1px solid #F3F4F6' },
  previewMore: { padding:'8px 14px', fontSize:11, color:'#9CA3AF', fontStyle:'italic', textAlign:'center', borderTop:'1px solid #F3F4F6' },
  woNum:    { fontWeight:600, color:'#2D5C3F', minWidth:90 },
  woDate:   { color:'#6B7280', minWidth:100 },
  woTech:   { color:'#6B7280', flex:1, textAlign:'right' },
  radioGroup: { display:'flex', flexDirection:'column', gap:8 },
  radio:    { display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px', border:'2px solid', borderRadius:8, cursor:'pointer', transition:'all 0.15s' },
  radioTitle: { fontWeight:600, fontSize:13, color:'#1F2937' },
  radioDesc:  { fontSize:11, color:'#6B7280', marginTop:2 },
  footer:   { padding:'14px 20px', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'flex-end', gap:10, background:'#FAFAFA' },
  btnCancel: { padding:'9px 16px', background:'#F3F4F6', color:'#374151', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnPrimary:{ padding:'9px 16px', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:700 },
};

export default StopRecurringModal;
